/**
 * Implementação do repositório de Service: orquestra datasource remoto e local.
 */

import type { Service } from '@/core/domain';
import type { ServiceRepository } from '../../domain/repositories/service-repository';
import type { ServiceRemoteDataSource } from '../datasources/remote';
import type { ServiceLocalDataSource } from '../datasources/local';

export class ServiceRepositoryImpl implements ServiceRepository {
  constructor(
    private readonly remote: ServiceRemoteDataSource,
    private readonly local: ServiceLocalDataSource
  ) {}

  async getById(userId: string, id: string): Promise<Service | null> {
    const fromCache = await this.local.getById(id);
    if (fromCache != null) return fromCache;
    const fromRemote = await this.remote.getById(userId, id);
    if (fromRemote != null) await this.local.create(fromRemote).catch(() => {});
    return fromRemote;
  }

  async getAllByUserId(userId: string): Promise<Service[]> {
    return this.getAll(userId, false);
  }

  async getAll(userId: string, forceRefresh = false): Promise<Service[]> {
    if (!forceRefresh) {
      const fromCache = await this.local.getAll(userId);
      if (fromCache.length > 0) return fromCache;
    }
    const fromRemote = await this.remote.getAll(userId);
    await this.local.cacheAll(userId, fromRemote).catch(() => {});
    return fromRemote;
  }

  async create(userId: string, service: Service): Promise<Service> {
    const created = await this.remote.create(userId, service);
    await this.local.create(created).catch(() => {});
    return created;
  }

  async update(userId: string, id: string, service: Partial<Service>): Promise<Service> {
    const updated = await this.remote.update(userId, id, service);
    await this.local.update(id, updated).catch(() => {});
    return updated;
  }

  async delete(userId: string, id: string): Promise<void> {
    await this.remote.delete(userId, id);
    await this.local.delete(id).catch(() => {});
  }
}
